import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useColorModeValue, useToken } from "@chakra-ui/react";
import type { Container } from "@tsparticles/engine";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import { MAIN_BG_DARK } from "@/lib/chakra/theme";
import { useAppSelector } from "@/lib/hooks";

export const SignInParticles = memo(function SignInParticles() {
  const [init, setInit] = useState(false);
  const [accentColor] = useToken("colors", ["accentColor"]);
  const { clientType } = useAppSelector((state) => state.app);
  const bgColor = useColorModeValue(
    "#f4f4f5",
    clientType ? MAIN_BG_DARK[clientType] : "#1a202c"
  );
  const linksColor = useColorModeValue("#253f60", "#cbd5e0");

  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => {
      setInit(true);
    });
  }, [accentColor]);

  const particlesLoaded = useCallback(async (_container?: Container) => {
    // Optional: Add any logic when particles are loaded
  }, []);

  const particlesOptions = useMemo(
    () => ({
      background: {
        color: {
          value: bgColor,
        },
      },
      fpsLimit: 120,
      interactivity: {
        events: {
          onClick: {
            enable: true,
            mode: "push",
          },
          onHover: {
            enable: true,
            mode: "repulse",
          },
          resize: {
            enable: true,
          },
        },
        modes: {
          push: {
            quantity: 4,
          },
          repulse: {
            distance: 100,
            duration: 0.4,
          },
        },
      },
      particles: {
        color: {
          value: accentColor,
        },
        links: {
          color: linksColor,
          distance: 150,
          enable: true,
          opacity: 0.4,
          width: 1,
        },
        move: {
          direction: "none" as const,
          enable: true,
          outModes: {
            default: "bounce" as const,
          },
          random: false,
          speed: 2,
          straight: false,
        },
        number: {
          density: {
            enable: true,
            area: 800,
          },
          value: 150,
        },
        opacity: {
          value: 0.6,
        },
        shape: {
          type: "circle",
        },
        size: {
          value: { min: 1, max: 5 },
        },
      },
      detectRetina: true,
    }),
    [accentColor, bgColor, linksColor]
  );

  return init ? (
    <Particles
      id="tsparticles"
      particlesLoaded={particlesLoaded}
      options={particlesOptions}
    />
  ) : null;
});
